import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Lock,
  Clock,
  CheckCircle,
  XCircle,
  Copy,
  Check,
  UploadCloud,
  Eye,
  AlertTriangle,
  Smartphone,
  Building,
  ArrowLeft,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { getDisplayName } from "../utils/metalNameFixer";
import API_BASE from "../config/api";

const PaymentGatewayPage = () => {
  const { tradeId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedField, setCopiedField] = useState("");

  // Payment form state
  const [paymentMethod, setPaymentMethod] = useState("UPI");
  const [utr, setUtr] = useState("");
  const [screenshotBase64, setScreenshotBase64] = useState("");
  const [screenshotFileName, setScreenshotFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Track if initial load is done so status polling never resets user-selected payment tab or inputs
  const isInitialFetch = useRef(true);

  // Locked countdown timer (10 mins)
  const [timeLeft, setTimeLeft] = useState(600);

  // Bank & UPI Details
  const UPI_ID = "PIYUSHSONI88@YBL";
  const UPI_NAME = "GJ BULLION";
  const BANK_DETAILS = {
    bankName: "HDFC BANK",
    accountName: "PIYUSH SONI",
    accountNumber: "50200102486672",
    ifsc: "HDFC0007516",
    branch: "JOBAT / INDORE",
    accountType: "Current / Trading Account",
  };

  // 1. Fetch trade details
  const fetchTrade = async (isPolling = false) => {
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/api/trades/${tradeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.message || "Failed to load trade");
      }

      const data = await res.json();
      setTrade(data);

      // Only initialize user input fields on initial page load if trade was already submitted
      // Polling calls must never overwrite user's selected tab (paymentMethod) or typed UTR
      if (!isPolling && isInitialFetch.current) {
        isInitialFetch.current = false;
        if (data.paymentMethod && data.status !== "pending_payment") {
          setPaymentMethod(data.paymentMethod);
        }
        if (data.utr) setUtr(data.utr);
        if (data.paymentScreenshot) setScreenshotBase64(data.paymentScreenshot);
      }
    } catch (err) {
      console.error("Trade fetch error:", err);
      if (!isPolling) {
        setError(err.message || "Failed to load payment details");
      }
    } finally {
      if (!isPolling) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isInitialFetch.current = true;
    fetchTrade(false);
  }, [tradeId]);

  // 2. Auto-polling for live verification status (does NOT touch user input / paymentMethod)
  useEffect(() => {
    if (!trade) return;
    if (trade.status === "confirmed" || trade.status === "declined") return;

    const interval = setInterval(() => {
      fetchTrade(true);
    }, 4000);

    return () => clearInterval(interval);
  }, [trade?.status, tradeId]);

  // 3. Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // One-click copy helper
  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(""), 2000);
  };

  // Client-side image compression & conversion to Base64
  const handleScreenshotChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file (PNG, JPG, JPEG, WEBP)");
      return;
    }

    setScreenshotFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 1200;
        const scaleSize = MAX_WIDTH / Math.max(img.width, MAX_WIDTH);
        canvas.width = img.width * scaleSize;
        canvas.height = img.height * scaleSize;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Compress to high quality JPEG data URL
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setScreenshotBase64(dataUrl);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Submit payment proof
  const handleSubmitProof = async (e) => {
    e.preventDefault();
    if (!utr && !screenshotBase64) {
      alert("Please provide either your UTR / Transaction reference or attach the payment screenshot.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const res = await fetch(
        `${API_BASE}/api/trades/${tradeId}/submit-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentMethod,
            utr,
            paymentScreenshot: screenshotBase64,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to submit payment proof");
      }

      setSubmitSuccess(true);
      setTrade(data.trade);
      alert("✅ Payment proof submitted successfully! Verification is pending with admin desk.");
      navigate("/trades");
    } catch (err) {
      console.error("Payment submit error:", err);
      setError(err.message || "Error submitting payment proof");
    } finally {
      setSubmitting(false);
    }
  };

  // Generate Dynamic UPI Link
  const amount = trade?.totalPrice || 0;
  const upiIntentUrl = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(
    UPI_NAME
  )}&am=${amount}&cu=INR&tn=${encodeURIComponent(
    trade?.tradeId || "GJ_Gold_Order"
  )}`;

  // Dynamic QR Code URL
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(
    upiIntentUrl
  )}`;

  if (loading) {
    return (
      <Container>
        <LoaderCard>
          <RefreshCw className="spinner" size={36} color="#f5d76e" />
          <p>Connecting to secure payment desk...</p>
        </LoaderCard>
      </Container>
    );
  }

  if (error && !trade) {
    return (
      <Container>
        <ErrorCard>
          <AlertTriangle size={42} color="#ef4444" />
          <h2>Order Not Found or Unauthorized</h2>
          <p>{error}</p>
          <BackButton onClick={() => navigate("/trades")}>
            <ArrowLeft size={16} /> Back to Trade History
          </BackButton>
        </ErrorCard>
      </Container>
    );
  }

  const isConfirmed = trade?.status === "confirmed";
  const isDeclined = trade?.status === "declined";
  const isPendingVerification = trade?.status === "pending_verification";
  const isProofSubmitted = isPendingVerification || isConfirmed || isDeclined;

  return (
    <Container>
      <Navbar>
        <NavInner>
          <Brand onClick={() => navigate("/")}>
            <img src="/assets/logo.jpeg" alt="GJ Bullion" />
            <span>GJ BULLION</span>
          </Brand>
          <SecurityBadge>
            <ShieldCheck size={16} color="#10b981" />
            <span>256-Bit SSL Encrypted Desk</span>
          </SecurityBadge>
        </NavInner>
      </Navbar>

      <ContentWrapper>
        {/* Top Header Summary */}
        <TopBar>
          <BackButton onClick={() => navigate("/trades")}>
            <ArrowLeft size={16} /> Back to Trades
          </BackButton>
          <div className="order-id">
            {trade?.tradeId ? (
              <>Trade ID: <span>{trade.tradeId}</span></>
            ) : (
              <>Order Status: <span>Awaiting Payment Proof</span></>
            )}
          </div>
        </TopBar>

        {/* Status Alert if Confirmed or Declined */}
        {isConfirmed && (
          <StatusBanner className="confirmed">
            <CheckCircle size={24} />
            <div>
              <h4>🎉 Payment Verified & Trade Confirmed!</h4>
              <p>Your trade has been officially verified by GJ Bullion Admin.</p>
            </div>
            <button onClick={() => navigate("/trades")}>View Trade History</button>
          </StatusBanner>
        )}

        {isDeclined && (
          <StatusBanner className="declined">
            <XCircle size={24} />
            <div>
              <h4>❌ Trade Payment Declined</h4>
              <p>{trade.declineReason || "Payment could not be verified in bank records."}</p>
            </div>
          </StatusBanner>
        )}

        {isPendingVerification && !isConfirmed && (
          <StatusBanner className="pending">
            <Clock size={24} className="pulse" />
            <div>
              <h4>⏳ Payment Proof Submitted - Verification Pending</h4>
              <p>
                Our admin desk is actively verifying your transaction (UTR:{" "}
                <strong>{trade.utr || "Attached"}</strong>). This page will update automatically!
              </p>
            </div>
          </StatusBanner>
        )}

        <MainGrid>
          {/* LEFT: Order Summary & Locked Amount Card */}
          <LeftCol>
            <OrderLockCard>
              <LockHeader>
                <div className="badge">
                  <Lock size={14} /> AMOUNT LOCKED
                </div>
                <div className="timer">
                  <Clock size={14} /> Price Locked ({formatTimer(timeLeft)})
                </div>
              </LockHeader>

              <AmountDisplay>
                <span className="currency">₹</span>
                <span className="value">{amount.toLocaleString("en-IN")}</span>
              </AmountDisplay>

              <OrderDetailsList>
                <div className="detail-row">
                  <span className="lbl">Metal</span>
                  <span className="val highlight">{getDisplayName(trade?.metal)}</span>
                </div>
                <div className="detail-row">
                  <span className="lbl">Order Type</span>
                  <span className={`val action-${trade?.action}`}>
                    {trade?.action?.toUpperCase()} ORDER
                  </span>
                </div>
                <div className="detail-row">
                  <span className="lbl">Quantity</span>
                  <span className="val">{trade?.quantity}</span>
                </div>
                <div className="detail-row">
                  <span className="lbl">Locked Rate</span>
                  <span className="val">₹ {trade?.rate?.toLocaleString("en-IN")}</span>
                </div>
                <div className="detail-row">
                  <span className="lbl">Customer Name</span>
                  <span className="val">{trade?.fullName}</span>
                </div>
                <div className="detail-row">
                  <span className="lbl">Phone</span>
                  <span className="val">{trade?.phone}</span>
                </div>
              </OrderDetailsList>

              <SecurityNote>
                🛡️ This locked amount is tamper-proof and verified directly against live Indore Bullion benchmark rates.
              </SecurityNote>
            </OrderLockCard>
          </LeftCol>

          {/* RIGHT: Payment Modes & Verification Proof Upload */}
          <RightCol>
            {/* Payment Method Switcher Tabs */}
            <MethodTabs>
              <TabBtn
                $active={paymentMethod === "UPI"}
                onClick={() => !isProofSubmitted && setPaymentMethod("UPI")}
                style={isProofSubmitted ? { cursor: "default", opacity: paymentMethod === "UPI" ? 1 : 0.4 } : {}}
              >
                <Smartphone size={16} /> UPI Apps & QR
              </TabBtn>
              <TabBtn
                $active={paymentMethod === "Bank Transfer"}
                onClick={() => !isProofSubmitted && setPaymentMethod("Bank Transfer")}
                style={isProofSubmitted ? { cursor: "default", opacity: paymentMethod === "Bank Transfer" ? 1 : 0.4 } : {}}
              >
                <Building size={16} /> Net Banking / IMPS / RTGS
              </TabBtn>
            </MethodTabs>

            {/* UPI Option */}
            {paymentMethod === "UPI" && (
              <PaymentBox>
                <BoxHeading>
                  <span>1. Pay using any UPI Application</span>
                </BoxHeading>

                {/* Mobile Direct UPI Intent Button */}
                <UpiIntentBtn
                  href={upiIntentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Smartphone size={18} />
                  <span>Tap to Pay ₹{amount.toLocaleString("en-IN")} via UPI App</span>
                </UpiIntentBtn>
                <HintText>Opens Google Pay, PhonePe, Paytm, or BHIM directly on your phone with amount pre-filled.</HintText>

                <Divider>
                  <span>OR SCAN QR CODE</span>
                </Divider>

                {/* Dynamic QR Code */}
                <QrContainer>
                  <div className="qr-wrapper">
                    <img src={qrCodeUrl} alt="GJ Bullion UPI QR" />
                  </div>
                  <div className="qr-info">
                    <div className="upi-field">
                      <span className="title">UPI ID:</span>
                      <span className="val">{UPI_ID}</span>
                      <CopyBtn onClick={() => handleCopy(UPI_ID, "upi")}>
                        {copiedField === "upi" ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                        {copiedField === "upi" ? "Copied" : "Copy"}
                      </CopyBtn>
                    </div>

                    <div className="upi-field">
                      <span className="title">Payee Name:</span>
                      <span className="val">{UPI_NAME}</span>
                    </div>

                    <div className="upi-field">
                      <span className="title">Exact Amount:</span>
                      <span className="val highlight">₹ {amount.toLocaleString("en-IN")}</span>
                      <CopyBtn onClick={() => handleCopy(amount.toString(), "amount")}>
                        {copiedField === "amount" ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                        {copiedField === "amount" ? "Copied" : "Copy"}
                      </CopyBtn>
                    </div>
                  </div>
                </QrContainer>
              </PaymentBox>
            )}

            {/* Bank Transfer Option */}
            {paymentMethod === "Bank Transfer" && (
              <PaymentBox>
                <BoxHeading>
                  <span>1. Transfer to Official Bank Account</span>
                </BoxHeading>

                <BankCard>
                  <div className="bank-row">
                    <span className="lbl">Bank Name:</span>
                    <span className="val">{BANK_DETAILS.bankName}</span>
                  </div>
                  <div className="bank-row">
                    <span className="lbl">Account Holder:</span>
                    <span className="val highlight">{BANK_DETAILS.accountName}</span>
                  </div>
                  <div className="bank-row">
                    <span className="lbl">Account Number:</span>
                    <span className="val mono">{BANK_DETAILS.accountNumber}</span>
                    <CopyBtn onClick={() => handleCopy(BANK_DETAILS.accountNumber, "acc")}>
                      {copiedField === "acc" ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                      {copiedField === "acc" ? "Copied" : "Copy"}
                    </CopyBtn>
                  </div>
                  <div className="bank-row">
                    <span className="lbl">IFSC Code:</span>
                    <span className="val mono">{BANK_DETAILS.ifsc}</span>
                    <CopyBtn onClick={() => handleCopy(BANK_DETAILS.ifsc, "ifsc")}>
                      {copiedField === "ifsc" ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                      {copiedField === "ifsc" ? "Copied" : "Copy"}
                    </CopyBtn>
                  </div>
                  <div className="bank-row">
                    <span className="lbl">Account Type:</span>
                    <span className="val">{BANK_DETAILS.accountType}</span>
                  </div>
                </BankCard>
                <HintText>Transfer exact amount ₹{amount.toLocaleString("en-IN")} using IMPS, NEFT, or RTGS.</HintText>
              </PaymentBox>
            )}

            {/* STEP 2: Attach Screenshot & Reference Proof */}
            <ProofCard onSubmit={handleSubmitProof}>
              <BoxHeading>
                <span>2. Attach Proof for Instant Verification</span>
              </BoxHeading>

              <FormGroup>
                <label>
                  UPI Reference No. / Bank UTR No. <span className="req">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter 12-digit UTR or Transaction Reference ID"
                  value={utr}
                  onChange={(e) => setUtr(e.target.value)}
                  disabled={isProofSubmitted}
                  readOnly={isProofSubmitted}
                />
              </FormGroup>

              <FormGroup>
                <label>
                  Attach Payment Screenshot <span className="req">*</span>
                </label>
                <UploadZone>
                  <input
                    type="file"
                    id="screenshot-input"
                    accept="image/*"
                    onChange={handleScreenshotChange}
                    disabled={isProofSubmitted}
                  />
                  <label
                    htmlFor={isProofSubmitted ? "" : "screenshot-input"}
                    className="upload-label"
                    style={isProofSubmitted ? { cursor: "default", opacity: 0.75 } : {}}
                  >
                    <UploadCloud size={24} color="#f5d76e" />
                    <span className="main">
                      {screenshotFileName
                        ? screenshotFileName
                        : isProofSubmitted
                        ? "Payment screenshot submitted & locked"
                        : "Click or tap to upload payment screenshot"}
                    </span>
                    <span className="sub">
                      {isProofSubmitted
                        ? "Screenshot is locked against modifications to prevent mismatches"
                        : "Supports JPG, PNG, WEBP (Instant auto-compression)"}
                    </span>
                  </label>
                </UploadZone>

                {screenshotBase64 && (
                  <ScreenshotPreview>
                    <img src={screenshotBase64} alt="Payment Proof Preview" />
                    <button
                      type="button"
                      className="view-btn"
                      onClick={() => setPreviewModalOpen(true)}
                    >
                      <Eye size={14} /> Enlarge View
                    </button>
                    {!isProofSubmitted && (
                      <button
                        type="button"
                        className="del-btn"
                        onClick={() => {
                          setScreenshotBase64("");
                          setScreenshotFileName("");
                        }}
                      >
                        ✕ Remove
                      </button>
                    )}
                  </ScreenshotPreview>
                )}
              </FormGroup>

              {error && <FormError>{error}</FormError>}

              {!isProofSubmitted ? (
                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <RefreshCw className="spinner" size={16} /> Submitting Proof...
                    </>
                  ) : (
                    "Confirm & Submit Payment Verification"
                  )}
                </SubmitButton>
              ) : (
                <LockedProofNotice>
                  <Lock size={16} />
                  <span>
                    {isConfirmed
                      ? "Trade confirmed and payment verified by Admin."
                      : isDeclined
                      ? "Trade payment was declined by Admin."
                      : "Payment proof submitted & locked for admin desk verification."}
                  </span>
                </LockedProofNotice>
              )}

              {submitSuccess && (
                <SuccessMsg>
                  ✅ Proof submitted successfully! Admin desk is reviewing your payment.
                </SuccessMsg>
              )}
            </ProofCard>
          </RightCol>
        </MainGrid>
      </ContentWrapper>

      {/* Fullscreen Image Preview Modal */}
      {previewModalOpen && (
        <ModalOverlay onClick={() => setPreviewModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setPreviewModalOpen(false)}>
              ✕
            </button>
            <img src={screenshotBase64} alt="Full Screenshot Preview" />
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default PaymentGatewayPage;

/* ======================================================
   💅 STYLED COMPONENTS (RESPONSIVE & MODERN DARK/GOLD)
   ====================================================== */

const Container = styled.div`
  background: #080b0f;
  color: #f1f5f9;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding-bottom: 3rem;
`;

const Navbar = styled.header`
  background: #0f141c;
  border-bottom: 1px solid #1e293b;
  padding: 0.85rem 1.25rem;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const NavInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  font-weight: 800;
  color: #f5d76e;
  font-size: 1.2rem;
  letter-spacing: 0.5px;

  img {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    object-fit: cover;
  }
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #10b981;
  font-weight: 600;
  background: rgba(16, 185, 129, 0.1);
  padding: 0.35rem 0.75rem;
  border-radius: 50px;
  border: 1px solid rgba(16, 185, 129, 0.25);
`;

const ContentWrapper = styled.div`
  max-width: 1100px;
  margin: 1.5rem auto 0;
  padding: 0 1rem;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 0.75rem;

  .order-id {
    font-size: 0.85rem;
    color: #94a3b8;
    span {
      color: #f5d76e;
      font-weight: 700;
      font-family: monospace;
    }
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #172033;
  color: #e2e8f0;
  border: 1px solid #334155;
  border-radius: 6px;
  padding: 0.45rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1e293b;
    color: #fff;
  }
`;

const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  font-size: 0.92rem;
  flex-wrap: wrap;

  h4 {
    margin: 0 0 0.2rem 0;
    font-size: 1rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
  }

  &.confirmed {
    background: rgba(16, 185, 129, 0.15);
    border: 1.5px solid #10b981;
    color: #10b981;
    p {
      color: #cbd5e1;
    }
    button {
      margin-left: auto;
      background: #10b981;
      color: #000;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      font-weight: 700;
      cursor: pointer;
    }
  }

  &.declined {
    background: rgba(239, 68, 68, 0.15);
    border: 1.5px solid #ef4444;
    color: #ef4444;
    p {
      color: #fca5a5;
    }
  }

  &.pending {
    background: rgba(245, 215, 110, 0.12);
    border: 1.5px solid #f5d76e;
    color: #f5d76e;
    p {
      color: #cbd5e1;
    }
  }

  .pulse {
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 860px) {
    grid-template-columns: 360px 1fr;
  }
`;

const LeftCol = styled.div``;

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const OrderLockCard = styled.div`
  background: #0f141c;
  border: 1.5px solid #f5d76e44;
  border-radius: 16px;
  padding: 1.4rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5);
  position: sticky;
  top: 5rem;
`;

const LockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  font-size: 0.78rem;

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(245, 215, 110, 0.15);
    color: #f5d76e;
    padding: 0.25rem 0.55rem;
    border-radius: 4px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .timer {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #94a3b8;
  }
`;

const AmountDisplay = styled.div`
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #1e293b;

  .currency {
    font-size: 1.5rem;
    color: #f5d76e;
    font-weight: 700;
    margin-right: 4px;
  }

  .value {
    font-size: 2.2rem;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.5px;
  }
`;

const OrderDetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  .detail-row {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;

    .lbl {
      color: #94a3b8;
    }

    .val {
      font-weight: 600;
      color: #f1f5f9;

      &.highlight {
        color: #f5d76e;
      }

      &.action-buy {
        color: #48ff91;
      }

      &.action-sell {
        color: #f87171;
      }
    }
  }
`;

const SecurityNote = styled.p`
  margin-top: 1.25rem;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.4;
  border-top: 1px dashed #1e293b;
  padding-top: 0.85rem;
`;

const MethodTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #0f141c;
  padding: 0.4rem;
  border-radius: 10px;
  border: 1px solid #1e293b;
`;

const TabBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active }) => ($active ? "#f5d76e" : "transparent")};
  color: ${({ $active }) => ($active ? "#000" : "#94a3b8")};

  &:hover {
    color: ${({ $active }) => ($active ? "#000" : "#fff")};
  }
`;

const PaymentBox = styled.div`
  background: #0f141c;
  border: 1px solid #1e293b;
  border-radius: 14px;
  padding: 1.4rem;
`;

const BoxHeading = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #f5d76e;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UpiIntentBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.85rem 1.2rem;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
  }
`;

const HintText = styled.p`
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0.6rem 0 0 0;
  text-align: center;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.25rem 0;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #1e293b;
  }

  span {
    padding: 0 0.75rem;
  }
`;

const QrContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;

  .qr-wrapper {
    background: #fff;
    padding: 8px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;

    img {
      width: 170px;
      height: 170px;
      display: block;
    }
  }

  .qr-info {
    flex: 1;
    min-width: 220px;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .upi-field {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: #172033;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    position: relative;

    .title {
      font-size: 0.7rem;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .val {
      font-size: 0.9rem;
      font-weight: 700;
      color: #f1f5f9;
      font-family: monospace;

      &.highlight {
        color: #f5d76e;
      }
    }
  }
`;

const BankCard = styled.div`
  background: #172033;
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  .bank-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.85rem;
    flex-wrap: wrap;
    gap: 0.35rem;

    .lbl {
      color: #94a3b8;
    }

    .val {
      font-weight: 600;
      color: #f1f5f9;

      &.highlight {
        color: #f5d76e;
      }

      &.mono {
        font-family: monospace;
        font-size: 0.92rem;
      }
    }
  }
`;

const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #0f172a;
  color: #e2e8f0;
  border: 1px solid #334155;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: 0.72rem;
  cursor: pointer;
  margin-left: auto;

  &:hover {
    background: #1e293b;
  }
`;

const ProofCard = styled.form`
  background: #0f141c;
  border: 1px solid #1e293b;
  border-radius: 14px;
  padding: 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  label {
    font-size: 0.85rem;
    color: #cbd5e1;
    font-weight: 600;

    .req {
      color: #f87171;
    }
  }

  input[type="text"] {
    background: #172033;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 0.7rem 0.9rem;
    color: #fff;
    font-size: 0.9rem;

    &:focus {
      outline: none;
      border-color: #f5d76e;
    }
  }
`;

const UploadZone = styled.div`
  input[type="file"] {
    display: none;
  }

  .upload-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 1rem;
    background: #172033;
    border: 2px dashed #334155;
    border-radius: 10px;
    cursor: pointer;
    text-align: center;
    transition: all 0.2s ease;

    &:hover {
      border-color: #f5d76e;
      background: #1a243b;
    }

    .main {
      margin-top: 0.5rem;
      font-size: 0.88rem;
      font-weight: 600;
      color: #f1f5f9;
    }

    .sub {
      margin-top: 0.2rem;
      font-size: 0.75rem;
      color: #94a3b8;
    }
  }
`;

const ScreenshotPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #172033;
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #334155;
  margin-top: 0.5rem;

  img {
    width: 48px;
    height: 48px;
    border-radius: 6px;
    object-fit: cover;
  }

  button {
    border: none;
    padding: 0.35rem 0.7rem;
    border-radius: 5px;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;

    &.view-btn {
      background: #334155;
      color: #fff;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    &.del-btn {
      background: rgba(239, 68, 68, 0.2);
      color: #ef4444;
      margin-left: auto;
    }
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #f5d76e 0%, #eab308 100%);
  color: #000;
  border: none;
  border-radius: 10px;
  padding: 0.85rem 1.4rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 15px rgba(245, 215, 110, 0.3);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(245, 215, 110, 0.45);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const FormError = styled.div`
  color: #ef4444;
  font-size: 0.82rem;
  background: rgba(239, 68, 68, 0.1);
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
`;

const SuccessMsg = styled.div`
  color: #10b981;
  font-size: 0.88rem;
  font-weight: 600;
  background: rgba(16, 185, 129, 0.1);
  padding: 0.65rem 0.85rem;
  border-radius: 6px;
  text-align: center;
`;

const LockedProofNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: rgba(245, 215, 110, 0.08);
  border: 1px solid rgba(245, 215, 110, 0.3);
  color: #f5d76e;
  padding: 0.9rem 1.2rem;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  text-align: center;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: #0f141c;
  border-radius: 12px;
  padding: 0.5rem;
  border: 1.5px solid #f5d76e;

  img {
    max-width: 100%;
    max-height: 85vh;
    border-radius: 8px;
    display: block;
  }

  .close-btn {
    position: absolute;
    top: -12px;
    right: -12px;
    background: #ef4444;
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
`;

const LoaderCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 1rem;
  color: #94a3b8;

  .spinner {
    animation: spin 1s linear infinite;
  }
`;

const ErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 1rem;
  text-align: center;
  max-width: 480px;
  margin: 0 auto;

  h2 {
    color: #fff;
    margin: 0;
  }

  p {
    color: #94a3b8;
    margin: 0;
  }
`;
